import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { NotificationPreferenceDto } from '@myscaw/shared';
import { Announcement } from '../../features/announcement/shared/types';
import { subscribeDeviceToPush } from './push-subscription.client';
import { showToast } from '../ui/Toast';

interface PaginatedAnnouncements {
  items: Announcement[];
}

const STORAGE_KEY = 'myscaw_last_seen_announcement_id';

/**
 * Play a subtle, pleasant notification chime using the Web Audio API.
 * Synthesized dynamically, no external sound asset required.
 */
function playNotificationChime() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.08); // A5

    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.35);
  } catch {
    // Audio autoplay policy might silently suppress before first user interaction
  }
}

/**
 * Announcement notification listener:
 * 1. Shows an in-app visual toast banner when an announcement is posted.
 * 2. Triggers native OS / browser notification (via Service Worker on mobile or desktop).
 * 3. Plays a subtle audio chime.
 */
export function useAnnouncementNotifications() {
  const { user } = useAuth();

  // Check user preference from API
  const preferenceQuery = useQuery<NotificationPreferenceDto>({
    queryKey: ['notification-preference'],
    queryFn: () => api.get('/me/notification-preference'),
    enabled: !!user,
  });

  const isOptedIn = preferenceQuery.data?.announcementsOptIn ?? false;

  // Poll for latest announcements every 15 seconds if opted in
  const announcementsQuery = useQuery<PaginatedAnnouncements>({
    queryKey: ['announcements-recent-watch'],
    queryFn: () => api.get('/announcements?page=1&limit=1'),
    enabled: !!user && isOptedIn,
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  // Sync Web Push subscription for locked screen & background delivery when opted in
  useEffect(() => {
    if (!user) return;

    if (
      isOptedIn &&
      typeof window !== 'undefined' &&
      'Notification' in window &&
      Notification.permission === 'granted'
    ) {
      subscribeDeviceToPush().catch((err) => {
        console.warn('Auto-subscribe push error:', err);
      });
    }
  }, [user, isOptedIn]);

  useEffect(() => {
    if (!user || !isOptedIn) return;

    const latest = announcementsQuery.data?.items?.[0];
    if (!latest) return;

    const storedLastId = localStorage.getItem(STORAGE_KEY);
    const latestId = Number(latest.id);

    // Initial mount on fresh session: record the latest known ID and exit.
    // The user just opened the app (or tapped a push notification) — no duplicate alert.
    if (storedLastId === null) {
      localStorage.setItem(STORAGE_KEY, String(latestId));
      return;
    }

    const lastId = Number(storedLastId);
    if (latestId <= lastId) {
      return;
    }
    localStorage.setItem(STORAGE_KEY, String(latestId));

    // A brand new announcement was published while the user is actively using the platform
    playNotificationChime();

    // In-App Toast notification (elegantly informs the user inside the web app)
    showToast(latest.title, {
      variant: 'info',
      durationMs: 8000,
    });
  }, [user, isOptedIn, announcementsQuery.data]);
}
